import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {CARRIER_TYPE_KEYS} from '@/modules/smart-compounders/constants'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {FormGrid} from '@cypro/ui/components/ui/form-grid'
import {withForm} from '@cypro/ui/integrations/form'
import {BasicDataResources} from '@cypro/engine/schema'
import {CompoundMappingType} from '@cypro/engine/database/schema'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

interface DefaultValues {
  machineId: string
  carrierType: string
  /**
   * Solution types, if relevant.
   */
  types?: string[]
}

interface Props {
  mappingType: CompoundMappingType
}

// Shared "Smart compounder" section used inside the resource forms. Only the fields it
// manages live in defaultValues; the parent form is passed in via the `form` prop.
export const SmartCompounderSection = withForm({
  defaultValues: {machineId: '', carrierType: '0', types: []} as DefaultValues,
  props: {} as Props,
  render: function Render({form, mappingType}) {
    const {t} = useTranslate('common')
    const isCarrier = mappingType === CompoundMappingType.enum.CARRIER

    const {items: solutionTypes} = useGetBasicDataItems({resource: BasicDataResources.solutionTypes})
    const selectedTypes = useStore(form.store, state => state.values.types)

    const carrierSolutionTypeId = solutionTypes?.find(item => item.code === 'CARRIER_SOLUTION')?.id
    const isCarrierSolutionSelected = Boolean(carrierSolutionTypeId && selectedTypes?.includes(carrierSolutionTypeId))

    return (
      <FormSection label={t('smart_compounder')} description={t('smart_compounder_mapping_description')}>
        <FormGrid>
          <form.AppField name="machineId" mode="value">
            {field => (
              <field.TextField
                label={
                  <>
                    {t('smart_compounder_machine_id')}
                    <Tooltip content={t('smart_compounder_machine_id_description')} />
                  </>
                }
                placeholder={t('eg', {value: 'SMC-NA-001'})}
              />
            )}
          </form.AppField>

          {isCarrier && (
            <form.AppField name="carrierType" mode="value">
              {field => (
                <field.Select
                  label={
                    <>
                      {t('carrier_type')}
                      <Tooltip
                        content={
                          isCarrierSolutionSelected
                            ? t('smart_compounder_carrier_type_description')
                            : t('smart_compounder_carrier_type_requires_carrier_solution')
                        }
                      />
                    </>
                  }
                  items={Object.entries(CARRIER_TYPE_KEYS).map(([value, key]) => ({
                    label: t(key),
                    value: String(value),
                  }))}
                  disabled={!isCarrierSolutionSelected}
                />
              )}
            </form.AppField>
          )}
        </FormGrid>
      </FormSection>
    )
  },
})
